<template>
	<div class="documents-status-list" :class="{ 'is-embedded': !showHeader }">
		<header v-if="showHeader" class="list-header">
			<div class="list-title-group">
				<h2 class="list-title">Documents status</h2>
			</div>
		</header>

		<section class="filters-bar">
			<div class="filters-bar-start">
				<AppointmentIcon name="filter" />
			</div>

			<div v-if="documentFilter" class="filter-chip">
				<span class="filter-chip-text">{{ activeDocumentLabel }}</span>
				<button
					class="filter-chip-remove"
					type="button"
					aria-label="Clear document filter"
					@click.stop="documentFilter = ''"
				>
					<AppointmentIcon name="close" :size="14" />
				</button>
			</div>
			<div v-else class="filter-trigger">
				<button
					class="filter-label-btn"
					:class="{ 'is-active': editingFilterType === 'document' }"
					type="button"
					:disabled="controlsDisabled"
					@click.stop="toggleFilterEdit('document')"
				>
					Document
				</button>
				<div
					v-if="editingFilterType === 'document'"
					class="status-dropdown-wrapper"
					@click.stop
				>
					<div class="status-dropdown-backdrop" @click="editingFilterType = null"></div>
					<div class="status-dropdown status-dropdown--document">
						<button
							v-for="doc in documentOptions"
							:key="doc.id"
							type="button"
							class="status-dropdown-option"
							@pointerdown.prevent="selectDocument(String(doc.id))"
							@click="selectDocument(String(doc.id))"
						>
							{{ doc.title || 'Untitled document' }}
						</button>
						<div v-if="!documentOptions.length" class="status-dropdown-empty">
							No documents available
						</div>
					</div>
				</div>
			</div>

			<div v-if="statusFilter" class="filter-chip">
				<span class="filter-chip-text">{{ activeStatusLabel }}</span>
				<button
					class="filter-chip-remove"
					type="button"
					aria-label="Clear status filter"
					@click.stop="statusFilter = ''"
				>
					<AppointmentIcon name="close" :size="14" />
				</button>
			</div>
			<div v-else class="filter-trigger">
				<button
					class="filter-label-btn"
					:class="{ 'is-active': editingFilterType === 'status' }"
					type="button"
					:disabled="controlsDisabled"
					@click.stop="toggleFilterEdit('status')"
				>
					Status
				</button>
				<div
					v-if="editingFilterType === 'status'"
					class="status-dropdown-wrapper"
					@click.stop
				>
					<div class="status-dropdown-backdrop" @click="editingFilterType = null"></div>
					<div class="status-dropdown">
						<button
							v-for="status in statusOptions"
							:key="status.key"
							type="button"
							class="status-dropdown-option"
							@pointerdown.prevent="selectStatus(status.key)"
							@click="selectStatus(status.key)"
						>
							{{ status.label }}
						</button>
					</div>
				</div>
			</div>

			<div class="filter-search-wrapper">
				<AppointmentIcon name="search" :size="16" class="filter-search-icon" />
				<input
					v-model="search"
					type="text"
					class="filter-search-input"
					placeholder="Search supplier or document..."
					:disabled="controlsDisabled"
				/>
			</div>
		</section>

		<div v-if="showLoading" class="card placeholder">Loading documents status...</div>
		<div v-else-if="showError" class="card error">
			<p>{{ error }}</p>
			<span>Retrying automatically.</span>
		</div>

		<div v-else class="content-wrapper">
			<div class="table-wrapper">
				<table class="documents-status-table">
					<thead>
						<tr>
							<th scope="col" class="col-supplier">Supplier</th>
							<th scope="col" class="col-document">Document</th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="!supplierGroups.length">
							<td colspan="2" class="empty-cell">No supplier document statuses match your filters.</td>
						</tr>
						<tr
							v-for="group in supplierGroups"
							:key="group.id"
							class="supplier-row"
							:class="{ active: activeSubmenuGroupId === group.id }"
							@click="toggleSubmenu(group, $event)"
							@contextmenu.prevent="openSubmenu(group, $event)"
						>
							<td class="col-supplier" data-label="Supplier">
								<div class="supplier-cell">
									<div class="supplier-line">
										<span class="supplier-name">{{ group.supplierName }}</span>
										<span class="badge" :class="badgeClass(group.portalStatusTone)">
											{{ group.portalStatusLabel }}
										</span>
									</div>
								</div>
							</td>
							<td class="col-document" data-label="Document">
								<ul class="stack-list doc-list">
									<li
										v-for="doc in group.documents"
										:key="doc.id"
										class="stack-item"
										:title="doc.documentTitle"
									>
										<span class="document-line">
											<span class="document-title">{{ doc.documentTitle }}</span>
											<span class="document-status">
												<span class="badge" :class="badgeClass(doc.statusTone)">
													{{ doc.statusLabel }}
												</span>
												<span v-if="doc.completedAtDisplay" class="status-date">
													{{ doc.completedAtDisplay }}
												</span>
											</span>
										</span>
									</li>
								</ul>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div
				v-if="activeSubmenuGroup"
				class="submenu"
				:style="submenuStyle"
				v-click-outside="closeSubmenu"
			>
				<button class="submenu-btn" type="button" @click.stop="showActiveSupplierInCatalog">
					Show in catalog
				</button>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, reactive, ref } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import { useCatalogStore } from '@/stores/catalog';
import { useSupplierPortalAdminStore } from '@/stores/supplierPortalAdmin';
import { useSupplierPortalInsightsStore } from '@/stores/supplierPortalInsights';
import { useTabsStore } from '@/stores/tabs';
import { formatShortDate } from '@/utils/dateFormat';
import {
	DOCUMENT_STATUS_FILTER_OPTIONS,
	buildDocumentStatusRows,
	getActiveDocumentOptions,
} from '@/utils/documentsStatus';
import { onEvent } from '@/utils/eventBus';
import CatalogView from '@/views/catalog/CatalogView.vue';

defineProps({
	showHeader: {
		type: Boolean,
		default: true,
	},
});

const insightsStore = useSupplierPortalInsightsStore();
const adminStore = useSupplierPortalAdminStore();
const catalogStore = useCatalogStore();
const tabsStore = useTabsStore();
const instance = getCurrentInstance();
const toast = instance?.proxy?.$toast;
const logError = instance?.proxy?.$logError;

const search = ref('');
const documentFilter = ref('');
const statusFilter = ref('');
const editingFilterType = ref(null);

const renderedSuppliers = ref([]);
const renderedDocuments = ref([]);
const renderedSignature = ref('');
const isRefreshing = ref(false);
const activeSubmenuGroupId = ref(null);
const submenuPosition = reactive({ top: 0, left: 0 });

const AUTO_REFRESH_MS = 20000;
const CATALOG_MENU_TAB_KEY = 'catalogs';
let autoRefreshId = null;
let documentsUnsubscribe = null;

const hasLoadedData = computed(
	() => Boolean(renderedSuppliers.value.length) || Boolean(renderedDocuments.value.length),
);
const isLoadingData = computed(() => isRefreshing.value || insightsStore.loading || adminStore.loading);
const showLoading = computed(() => isLoadingData.value && !hasLoadedData.value);
const controlsDisabled = computed(() => showLoading.value);
const error = computed(() => insightsStore.error || adminStore.error || '');
const showError = computed(() => Boolean(error.value) && !hasLoadedData.value && !showLoading.value);
const statusOptions = DOCUMENT_STATUS_FILTER_OPTIONS;

const documentOptions = computed(() => getActiveDocumentOptions(renderedDocuments.value || []));

const activeDocumentLabel = computed(() => {
	if (!documentFilter.value) return '';
	const match = documentOptions.value.find((doc) => String(doc.id) === String(documentFilter.value));
	return match?.title || 'Document';
});

const activeStatusLabel = computed(() => {
	if (!statusFilter.value) return '';
	const match = statusOptions.find((status) => status.key === statusFilter.value);
	return match?.label || 'Status';
});

const toggleFilterEdit = (type) => {
	editingFilterType.value = editingFilterType.value === type ? null : type;
};

const formatCompletedDate = (value) => formatShortDate(value);

const selectDocument = (id) => {
	documentFilter.value = id;
	editingFilterType.value = null;
};

const selectStatus = (key) => {
	statusFilter.value = key;
	editingFilterType.value = null;
};

const rows = computed(() =>
	buildDocumentStatusRows({
		suppliers: renderedSuppliers.value,
		documents: renderedDocuments.value,
		selectedDocumentId: documentFilter.value,
	}),
);

const normalizeSearch = (value) => String(value || '').trim().toLowerCase();

const rowMatchesSearch = (row, query) => {
	if (!query) return true;
	return [
		row.supplierName,
		row.portalStatusLabel,
		row.documentTitle,
		row.documentFileName,
		row.typeLabel,
		row.statusLabel,
		row.completedAt,
	]
		.map(normalizeSearch)
		.join(' ')
		.includes(query);
};

const filteredRows = computed(() => {
	const query = normalizeSearch(search.value);
	const status = String(statusFilter.value || '').trim();
	return rows.value.filter((row) => {
		if (status && row.statusKey !== status) return false;
		return rowMatchesSearch(row, query);
	});
});

const supplierGroups = computed(() => {
	const groups = [];
	const indexByKey = new Map();
	const keyFor = (row) =>
		row.supplierId == null ? `name:${row.supplierName}` : `id:${row.supplierId}`;

	for (const row of filteredRows.value) {
		const key = keyFor(row);
		if (!indexByKey.has(key)) {
			indexByKey.set(key, groups.length);
			groups.push({
				id: key,
				supplierId: row.supplierId ?? null,
				supplierName: row.supplierName,
				portalStatusLabel: row.portalStatusLabel,
				portalStatusTone: row.portalStatusTone,
				documents: [],
			});
		}
		groups[indexByKey.get(key)].documents.push({
			id: row.id,
			documentId: row.documentId,
			documentTitle: row.documentTitle,
			statusKey: row.statusKey,
			statusLabel: row.statusLabel,
			statusTone: row.statusTone,
			completedAt: row.completedAt,
			completedAtDisplay:
				row.statusKey === 'completed' ? formatCompletedDate(row.completedAt) : '',
			assigned: row.assigned,
		});
	}

	return groups;
});

const activeSubmenuGroup = computed(
	() => supplierGroups.value.find((group) => group.id === activeSubmenuGroupId.value) || null,
);

const submenuStyle = computed(() => ({
	top: `${submenuPosition.top}px`,
	left: `${submenuPosition.left}px`,
}));

const buildRenderedSignature = () =>
	JSON.stringify({
		suppliers: insightsStore.suppliers || [],
		documents: adminStore.documents || [],
	});

const syncRenderedSnapshotIfChanged = ({ force = false } = {}) => {
	const nextSignature = buildRenderedSignature();
	if (!force && nextSignature === renderedSignature.value) return false;
	renderedSignature.value = nextSignature;
	renderedSuppliers.value = Array.isArray(insightsStore.suppliers)
		? JSON.parse(JSON.stringify(insightsStore.suppliers))
		: [];
	renderedDocuments.value = Array.isArray(adminStore.documents)
		? JSON.parse(JSON.stringify(adminStore.documents))
		: [];
	return true;
};

const refresh = async ({ forceSync = false } = {}) => {
	if (isRefreshing.value) return false;
	isRefreshing.value = true;
	const results = await Promise.allSettled([
		insightsStore.fetchOverview(),
		adminStore.fetchDocuments(),
	]);
	results.forEach((result) => {
		if (result.status === 'rejected') {
			logError?.(result.reason, 'DocumentsStatusListView::refresh');
		}
	});
	const changed = syncRenderedSnapshotIfChanged({ force: forceSync });
	isRefreshing.value = false;
	return changed;
};

const refreshIfVisible = () => {
	if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
	void refresh();
};

const handleVisibilityChange = () => {
	if (document.visibilityState === 'visible') {
		void refresh();
	}
};

const badgeClass = (tone) => {
	if (tone === 'success') return 'badge-success';
	if (tone === 'warning') return 'badge-warning';
	if (tone === 'danger') return 'badge-danger';
	return 'badge-muted';
};

const canOpenSupplierGroup = (group) =>
	group?.supplierId !== null && group?.supplierId !== undefined && group?.supplierId !== '';

const calculateSubmenuPosition = (event) => {
	const submenuWidth = 180;
	const submenuHeight = 52;
	const padding = 12;

	let left = event?.clientX || 0;
	let top = event?.clientY || 0;

	if (typeof window !== 'undefined') {
		if (left + submenuWidth > window.innerWidth - padding) {
			left = window.innerWidth - submenuWidth - padding;
		}
		if (top + submenuHeight > window.innerHeight - padding) {
			top = Math.max(padding, window.innerHeight - submenuHeight - padding);
		}
	}

	submenuPosition.left = Math.max(padding, left);
	submenuPosition.top = Math.max(padding, top);
};

const toggleSubmenu = (group, event) => {
	if (event) {
		event.stopPropagation();
		if (event.target?.closest?.('.submenu-btn')) {
			return;
		}
	}
	if (!canOpenSupplierGroup(group)) return;
	if (activeSubmenuGroupId.value === group.id) {
		closeSubmenu();
		return;
	}

	activeSubmenuGroupId.value = group.id;
	nextTick(() => {
		calculateSubmenuPosition(event);
	});
};

const openSubmenu = (group, event) => {
	if (event) {
		event.preventDefault();
		event.stopPropagation();
		if (event.target?.closest?.('.submenu-btn')) {
			return;
		}
	}
	if (!canOpenSupplierGroup(group)) return;

	activeSubmenuGroupId.value = group.id;
	nextTick(() => {
		calculateSubmenuPosition(event);
	});
};

const closeSubmenu = () => {
	activeSubmenuGroupId.value = null;
};

const openSupplierCatalogById = async (supplierId) => {
	if (supplierId === null || supplierId === undefined || supplierId === '') return;

	try {
		await catalogStore.bootstrap?.();
		const supplierCatalog = catalogStore.catalogByName?.('Suppliers');
		if (!supplierCatalog?.id) {
			toast?.error?.('Suppliers catalog not available');
			return;
		}

		catalogStore.setActiveCatalog(supplierCatalog.id);
		catalogStore.setActiveCatalogItemId(supplierCatalog.id, supplierId);

		const existing = tabsStore.findMatchingTab({
			component: CatalogView,
			itemId: supplierCatalog.id,
			section: 'menu',
			tabKey: CATALOG_MENU_TAB_KEY,
		});
		if (existing) {
			tabsStore.updateTab(existing.id, {
				props: { catalogId: supplierCatalog.id },
				title: supplierCatalog.name || 'Suppliers',
				itemId: supplierCatalog.id,
				section: 'menu',
				tabKey: CATALOG_MENU_TAB_KEY,
			});
			tabsStore.activateTab(existing.id);
			return;
		}

		const tab = tabsStore.createTab({
			component: CatalogView,
			title: supplierCatalog.name || 'Suppliers',
			props: { catalogId: supplierCatalog.id },
			itemId: supplierCatalog.id,
			section: 'menu',
			tabKey: CATALOG_MENU_TAB_KEY,
		});
		if (tab) {
			tabsStore.activateTab(tab.id);
		}
	} catch (error) {
		toast?.error?.('Unable to open supplier catalog');
		logError?.(error, 'DocumentsStatusListView::openSupplierCatalogById');
	}
};

const showActiveSupplierInCatalog = async () => {
	const supplierId = activeSubmenuGroup.value?.supplierId;
	closeSubmenu();
	await openSupplierCatalogById(supplierId);
};

const vClickOutside = {
	mounted(el, binding) {
		el.clickOutsideEvent = (event) => {
			if (!(el === event.target || el.contains(event.target))) {
				binding.value(event);
			}
		};
		document.body.addEventListener('click', el.clickOutsideEvent);
	},
	unmounted(el) {
		document.body.removeEventListener('click', el.clickOutsideEvent);
	},
};

// The page reloads on its own only while its tab is showing.
const startAutoRefresh = () => {
	if (autoRefreshId || typeof window === 'undefined') return;
	autoRefreshId = window.setInterval(refreshIfVisible, AUTO_REFRESH_MS);
	document.addEventListener('visibilitychange', handleVisibilityChange);
};

const stopAutoRefresh = () => {
	if (autoRefreshId && typeof window !== 'undefined') {
		window.clearInterval(autoRefreshId);
		autoRefreshId = null;
	}
	if (typeof document !== 'undefined') {
		document.removeEventListener('visibilitychange', handleVisibilityChange);
	}
};

let mountedAt = 0;

onMounted(() => {
	mountedAt = Date.now();
	syncRenderedSnapshotIfChanged({ force: true });
	void refresh({ forceSync: !hasLoadedData.value });
	startAutoRefresh();
	documentsUnsubscribe = onEvent('portal-documents:changed', () => {
		void refresh();
	});
});

// Coming back to the tab brings it up to date at once (its first showing already loaded it).
onActivated(() => {
	startAutoRefresh();
	if (Date.now() - mountedAt > 1000) void refresh();
});

onDeactivated(stopAutoRefresh);

onBeforeUnmount(() => {
	stopAutoRefresh();
	if (documentsUnsubscribe) {
		documentsUnsubscribe();
		documentsUnsubscribe = null;
	}
});
</script>

<style scoped>
/* Uses the filters bar of the journal pages and the list table of the Payments pages. */
.documents-status-list {
	display: flex;
	flex-direction: column;
	gap: 16px;
	min-height: 0;
	padding: 24px;
	color: var(--text-color);
	font: var(--ap-type-body);
}

.documents-status-list.is-embedded {
	padding: 0;
}

.list-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 16px;
	min-height: 40px;
}

.list-title-group {
	display: flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 12px;
}

.list-title {
	margin: 0;
	color: var(--text-color);
	font: var(--ap-type-title);
}

.filters-bar {
	position: relative;
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	min-height: 36px;
	padding: 2px 12px;
	box-sizing: border-box;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	box-shadow: none;
}

.filters-bar-start {
	display: flex;
	align-items: center;
	color: var(--text-color-secondary);
}

.filter-label-btn {
	display: inline-flex;
	align-items: center;
	height: 30px;
	padding: 0 10px;
	border: none;
	border-radius: 6px;
	background: none;
	color: var(--text-color-secondary);
	font: var(--ap-type-control);
	white-space: nowrap;
	cursor: pointer;
	transition: background-color 0.15s ease, color 0.15s ease;
}

.filter-label-btn:hover:not(:disabled),
.filter-label-btn.is-active {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-label-btn:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}

.filter-trigger {
	position: relative;
	display: inline-flex;
	align-items: center;
}

.filter-chip {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	max-width: 260px;
	height: 30px;
	padding: 0 4px 0 10px;
	box-sizing: border-box;
	border: 1px solid var(--border-color);
	border-radius: 6px;
	background: var(--noc-background, #fff);
	color: var(--text-color);
	font: var(--ap-type-control);
	white-space: nowrap;
}

.filter-chip-text {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.filter-chip-remove {
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	padding: 4px;
	border: none;
	border-radius: 4px;
	background: none;
	color: var(--text-color-secondary);
	cursor: pointer;
	transition: background-color 0.15s ease;
}

.filter-chip-remove:hover {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-search-wrapper {
	position: relative;
	display: flex;
	flex: 1 1 280px;
	align-items: center;
	min-width: 220px;
	height: 30px;
	margin-left: auto;
	border-radius: 6px;
	transition: background-color 0.15s ease;
}

.filter-search-wrapper:focus-within {
	background: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.filter-search-icon {
	position: absolute;
	top: 50%;
	left: 8px;
	color: var(--text-color-secondary);
	transform: translateY(-50%);
	pointer-events: none;
}

.filter-search-input {
	width: 100%;
	height: 30px;
	padding: 0 10px 0 32px;
	border: none;
	background: transparent;
	color: var(--text-color);
	font: var(--ap-type-body);
}

.filter-search-input::placeholder {
	color: var(--text-color-secondary);
	opacity: 0.7;
}

.filter-search-input:focus,
.filter-search-input:focus-visible {
	outline: none !important;
	box-shadow: none !important;
	border: none !important;
}

/* Filter lists open like the journal filter menus. */
.status-dropdown-wrapper {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	z-index: 100;
}

.status-dropdown-backdrop {
	position: fixed;
	inset: 0;
	z-index: 99;
}

.status-dropdown {
	position: relative;
	z-index: 100;
	display: flex;
	flex-direction: column;
	min-width: 160px;
	max-height: 320px;
	padding: 4px;
	overflow-y: auto;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background, #fff);
	box-shadow: 0 8px 24px rgba(25, 30, 45, 0.1);
	animation: filter-menu-enter 160ms ease-out both;
}

.status-dropdown--document {
	min-width: 240px;
}

.status-dropdown-option {
	display: block;
	width: 100%;
	padding: 7px 10px;
	overflow: hidden;
	border: none;
	border-radius: 6px;
	background: none;
	color: var(--text-color);
	font: var(--ap-type-body);
	text-align: left;
	text-overflow: ellipsis;
	white-space: nowrap;
	cursor: pointer;
	transition: background-color 0.15s ease;
}

.status-dropdown-option:hover {
	background: var(--noc-blue-05);
}

.status-dropdown-empty {
	padding: 10px 12px;
	color: var(--text-color-secondary);
	font: var(--ap-type-caption);
	text-align: center;
}

@keyframes filter-menu-enter {
	from {
		opacity: 0;
		transform: translateY(-4px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.card {
	margin: 0;
	padding: 12px 16px;
	color: var(--text-color-secondary);
	font: var(--ap-type-control);
}

.card.error {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	color: var(--noc-red, #c45151);
}

.card.error p {
	margin: 0;
}

.content-wrapper {
	display: flex;
	flex-direction: column;
	gap: 10px;
	min-height: 0;
}

.table-wrapper {
	display: flex;
	flex-direction: column;
	min-height: 0;
	overflow: hidden;
	border-inline: 1px solid var(--border-color);
	background: var(--noc-background, #fff);
	container-type: inline-size;
	container-name: documents-status-table;
}

.documents-status-table {
	width: 100%;
	table-layout: fixed;
	border-collapse: separate;
	border-spacing: 0;
	color: var(--text-color);
	font: var(--ap-type-body);
}

.documents-status-table th,
.documents-status-table td {
	padding: 10px 16px;
	border-bottom: 1px solid var(--border-color);
	text-align: left;
	vertical-align: middle;
}

.documents-status-table th {
	height: 38px;
	box-sizing: border-box;
	border-top: 1px solid var(--border-color);
	background: #fafafa;
	color: var(--text-color);
	font: var(--ap-type-table-label);
	font-weight: 600;
}

.documents-status-table tbody tr:not(:last-child) td:first-child {
	border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1;
}

.supplier-row {
	cursor: pointer;
	transition: background-color 0.15s ease;
}

.supplier-row.active {
	background: var(--noc-blue-05);
}

@media (hover: hover) {
	.supplier-row:hover {
		background: var(--noc-blue-05);
	}
}

.supplier-cell {
	display: flex;
	align-items: center;
	min-width: 0;
}

.supplier-line {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	min-width: 0;
	max-width: 100%;
}

.supplier-name {
	flex: 0 1 auto;
	min-width: 0;
	overflow: hidden;
	color: var(--text-color);
	font: var(--ap-type-control);
	text-overflow: ellipsis;
	white-space: nowrap;
}

.supplier-line > .badge {
	flex-shrink: 0;
}

.stack-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin: 0;
	padding: 0;
	list-style: none;
}

.stack-item {
	display: flex;
	align-items: center;
	min-width: 0;
	min-height: 24px;
}

.document-title {
	flex: 0 1 auto;
	min-width: 0;
	overflow: hidden;
	color: var(--text-color);
	font: var(--ap-type-control);
	text-overflow: ellipsis;
	white-space: nowrap;
}

.document-line {
	display: inline-flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	min-width: 0;
	max-width: 100%;
}

.document-status {
	display: inline-flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px;
	min-width: 0;
}

.document-status > .badge {
	flex-shrink: 0;
}

.status-date {
	color: var(--text-color-secondary);
	font: var(--ap-type-caption);
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}

.muted {
	color: var(--text-color-secondary);
}

.badge {
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	min-height: 22px;
	padding: 2px 8px;
	overflow: hidden;
	border-radius: 4px;
	font: var(--ap-type-label);
	font-weight: 600;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.badge-success {
	background: var(--report-status-success-background, rgba(39, 163, 118, 0.12));
	color: var(--report-status-success-color, #1f7f5d);
}

.badge-warning {
	background: var(--report-status-warning-background, rgba(205, 148, 48, 0.14));
	color: var(--report-status-warning-color, #9a6610);
}

.badge-danger {
	background: var(--report-status-danger-background, rgba(196, 81, 81, 0.12));
	color: var(--report-status-danger-color, #b33d3d);
}

.badge-muted {
	background: var(--report-status-muted-background, rgba(107, 114, 128, 0.12));
	color: var(--report-status-muted-color, #4b5563);
}

.empty-cell {
	padding: 24px 16px;
	color: var(--text-color-secondary);
	text-align: center;
}

.col-supplier {
	width: 32%;
}

.col-document {
	width: 68%;
}

/* The row menu opens like the journal row menus. */
.submenu {
	position: fixed;
	z-index: 1000;
	min-width: 160px;
	padding: 4px 0;
	border: 1px solid var(--border-color);
	border-radius: 4px;
	background-color: var(--noc-background);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	animation: filter-menu-enter 160ms ease-out both;
}

.submenu-btn {
	display: block;
	width: 100%;
	padding: 8px 16px;
	border: none;
	background: none;
	color: var(--text-color);
	font: var(--ap-type-control);
	text-align: left;
	white-space: nowrap;
	cursor: pointer;
}

.submenu-btn:hover {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

@media (prefers-reduced-motion: reduce) {
	.status-dropdown,
	.submenu {
		animation: none;
	}
}

/* Narrow: each supplier becomes a card when the table can't fit comfortably. */
@container documents-status-table (max-width: 760px) {
	.documents-status-table {
		display: block;
		table-layout: auto;
	}

	.documents-status-table thead {
		display: none;
	}

	.documents-status-table tbody {
		display: block;
	}

	.documents-status-table tbody tr.supplier-row {
		display: flex;
		flex-direction: column;
		row-gap: 12px;
		padding: 16px;
		border-top: 1px solid var(--border-color);
	}

	.documents-status-table tbody tr.supplier-row:first-child {
		border-top: none;
	}

	.documents-status-table td {
		display: block;
		width: auto;
		height: auto;
		min-width: 0;
		padding: 0 !important;
		border: none !important;
	}

	.col-supplier {
		padding-bottom: 8px !important;
		border-bottom: 1px dashed rgba(0, 0, 0, 0.07) !important;
	}

	.col-document {
		width: auto;
	}

	.stack-list {
		gap: 12px;
	}

	.documents-status-table tbody tr:has(> .empty-cell) {
		display: block;
		padding: 0;
	}

	.empty-cell {
		display: block;
		padding: 28px 16px;
	}
}
</style>
